import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import { toast } from "sonner";

import { useDropzone } from "react-dropzone";
import {
  FileText,
  Upload,
  CreditCard,
  LogOut,
  User,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Menu,
  X,
} from "lucide-react";

import VideoKYC from "../components/VideoKYC";
import PreApprovedOffers from "../components/PreApprovedOffers";

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [activeTab, setActiveTab] = useState("overview");
  const [applications, setApplications] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [appsRes, docsRes] = await Promise.all([
        api.get("/loans/my-applications"),
        api.get("/documents/my-documents"),
      ]);
      setApplications(Array.isArray(appsRes?.data) ? appsRes.data : []);
      setDocuments(Array.isArray(docsRes?.data) ? docsRes.data : []);
    } catch (err) {
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const statusColors = useMemo(
    () => ({
      draft: "bg-gray-500",
      submitted: "bg-blue-500",
      under_review: "bg-yellow-500",
      documents_required: "bg-orange-500",
      approved: "bg-green-500",
      rejected: "bg-red-500",
      disbursed: "bg-purple-500",
    }),
    []
  );

  const getStatusColor = (status) => statusColors[status] || "bg-gray-500";

  const getStatusIcon = (status) => {
    if (status === "approved" || status === "disbursed")
      return <CheckCircle size={20} />;
    if (status === "rejected") return <XCircle size={20} />;
    return <Clock size={20} />;
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles?.[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        toast.error("Max file size is 10MB");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", "other");

      try {
        await api.post("/documents/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Document uploaded successfully");
        fetchData();
      } catch (err) {
        toast.error("Failed to upload document");
      }
    },
    [fetchData]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/pdf": [".pdf"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
  });

  const downloadDocument = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download document");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#354F37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-[#354F37]">
                OJOVEST
              </Link>
            </div>

            <div className="hidden md:flex items-center space-x-4">
              <span className="text-gray-700">
                Welcome, {user?.full_name || "User"}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition"
              >
                <LogOut size={20} />
                Logout
              </button>
            </div>

            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 px-4 py-4">
            <p className="text-gray-700 font-semibold">{user?.full_name}</p>
            <button
              onClick={handleLogout}
              className="mt-3 flex items-center gap-2 text-red-600"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        )}
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <button
            onClick={() => navigate("/apply-loan")}
            className="bg-[#354F37] text-white p-6 rounded-xl hover:bg-[#426045] transition flex items-center gap-4"
          >
            <FileText size={32} />
            <div className="text-left">
              <h3 className="font-bold text-lg">Apply for Loans</h3>
              <p className="text-sm text-white/80">Start new application</p>
            </div>
          </button>

          <button
            onClick={() => navigate("/credit-cards")}
            className="bg-[#354F37] text-white p-6 rounded-xl hover:bg-[#426045] transition flex items-center gap-4"
          >
            <CreditCard size={32} />
            <div className="text-left">
              <h3 className="font-bold text-lg">Credit Cards</h3>
              <p className="text-sm text-white/80">Explore elite cards</p>
            </div>
          </button>

          <div className="bg-white p-6 rounded-xl border border-gray-200 flex items-center gap-4">
            <TrendingUp size={32} className="text-[#354F37]" />
            <div className="text-left">
              <h3 className="font-bold text-lg">{applications.length}</h3>
              <p className="text-sm text-gray-600">Total Applications</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <div className="flex overflow-x-auto">
              {[
                ["overview", "Overview"],
                ["applications", "My Applications"],
                ["documents", "Documents"],
                ["profile", "Profile"],
                ["kyc", "Video KYC"],
                ["offers", "Offers"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-6 py-4 font-semibold border-b-2 transition ${
                    activeTab === key
                      ? "border-[#354F37] text-[#354F37]"
                      : "border-transparent text-gray-600 hover:text-[#354F37]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Overview */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  Dashboard Overview
                </h2>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Total Applications</p>
                    <p className="text-2xl font-bold text-[#354F37]">
                      {applications.length}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Approved</p>
                    <p className="text-2xl font-bold text-green-600">
                      {
                        applications.filter((a) => a.status === "approved")
                          .length
                      }
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Under Review</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {
                        applications.filter(
                          (a) =>
                            a.status === "under_review" ||
                            a.status === "submitted"
                        ).length
                      }
                    </p>
                  </div>
                </div>

                {applications.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText size={64} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      No applications yet
                    </h3>
                    <p className="text-gray-500 mb-4">
                      Start your journey by applying for a premium loan.
                    </p>
                    <button
                      onClick={() => navigate("/apply-loan")}
                      className="bg-[#354F37] text-white px-6 py-3 rounded-lg hover:bg-[#426045] transition"
                    >
                      Apply Now
                    </button>
                  </div>
                ) : (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">
                      Recent Applications
                    </h3>
                    <div className="space-y-3">
                      {applications.slice(0, 3).map((app) => (
                        <div
                          key={app.id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-semibold text-gray-800">
                                {(app.loan_type || "")
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (c) => c.toUpperCase())}
                              </h4>
                              <p className="text-sm text-gray-600">
                                ₹
                                {Number(app.loan_amount || 0).toLocaleString(
                                  "en-IN"
                                )}
                              </p>
                            </div>
                            <span
                              className={`${getStatusColor(
                                app.status
                              )} text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-2`}
                            >
                              {getStatusIcon(app.status)}
                              {(app.status || "unknown").replace(/_/g, " ")}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">
                            Applied on{" "}
                            {app.created_at
                              ? new Date(app.created_at).toLocaleDateString(
                                  "en-IN"
                                )
                              : "-"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Applications */}
            {activeTab === "applications" && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  My Applications
                </h2>

                {applications.length === 0 ? (
                  <p className="text-gray-500">No applications yet</p>
                ) : (
                  <div className="space-y-4">
                    {applications.map((app) => (
                      <div
                        key={app.id}
                        className="border border-gray-200 rounded-lg p-6"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">
                              {(app.loan_type || "")
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </h3>
                            <p className="text-sm text-gray-600">
                              Application ID: {String(app.id).slice(0, 8)}…
                            </p>
                          </div>

                          <span
                            className={`${getStatusColor(
                              app.status
                            )} text-white px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2`}
                          >
                            {getStatusIcon(app.status)}
                            {(app.status || "unknown").replace(/_/g, " ")}
                          </span>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <p className="text-sm text-gray-600">
                              Requested Amount
                            </p>
                            <p className="font-semibold text-gray-800">
                              ₹
                              {Number(app.loan_amount || 0).toLocaleString(
                                "en-IN"
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">
                              Monthly Income
                            </p>
                            <p className="font-semibold text-gray-800">
                              ₹
                              {Number(app.monthly_income || 0).toLocaleString(
                                "en-IN"
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="text-sm text-gray-600 mt-4 space-y-1">
                          <p>
                            <strong>Purpose:</strong> {app.loan_purpose || "-"}
                          </p>
                          <p>
                            <strong>Employment:</strong>{" "}
                            {app.employment_type || "-"}
                          </p>
                          <p>
                            <strong>Applied:</strong>{" "}
                            {app.created_at
                              ? new Date(app.created_at).toLocaleDateString(
                                  "en-IN"
                                )
                              : "-"}
                          </p>
                        </div>

                        {app.admin_notes && (
                          <div className="mt-4 bg-blue-50 p-3 rounded-lg">
                            <p className="text-sm font-semibold text-blue-800">
                              Admin Notes:
                            </p>
                            <p className="text-sm text-blue-700">
                              {app.admin_notes}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Documents */}
            {activeTab === "documents" && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  My Documents
                </h2>

                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-8 mb-6 text-center cursor-pointer transition ${
                    isDragActive
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300 hover:border-blue-400"
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload size={48} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-700 font-semibold">
                    {isDragActive
                      ? "Drop the file here"
                      : "Drag & drop files here, or click to select"}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Support for PDF, JPG, PNG (Max 10MB)
                  </p>
                </div>

                {documents.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No documents uploaded yet
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center gap-3">
                          <FileText size={24} className="text-[#354F37]" />
                          <div>
                            <p className="font-semibold text-gray-800">
                              {doc.file_name || "Document"}
                            </p>
                            <p className="text-sm text-gray-500">
                              {(doc.document_type || "other").replace(/_/g, " ")}{" "}
                              • Uploaded{" "}
                              {doc.uploaded_at
                                ? new Date(doc.uploaded_at).toLocaleDateString(
                                    "en-IN"
                                  )
                                : "-"}
                            </p>
                          </div>
                        </div>

                        <button
                          onClick={() => downloadDocument(doc)}
                          className="text-[#354F37] hover:text-blue-700 font-semibold"
                        >
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Profile */}
            {activeTab === "profile" && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  Profile Information
                </h2>

                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
                    <User className="w-16 h-16 bg-blue-600 rounded-full p-4 text-white" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800">
                        {user?.full_name || "-"}
                      </h3>
                      <p className="text-gray-600">{user?.email || "-"}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-semibold text-gray-800">
                        {user?.phone || "-"}
                      </p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600">Account Type</p>
                      <p className="font-semibold text-gray-800 capitalize">
                        {user?.account_type || "-"}
                      </p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600">KYC Status</p>
                      <p className="font-semibold text-gray-800">
                        {user?.kyc_verified ? "Verified" : "Pending Verification"}
                      </p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600">Member Since</p>
                      <p className="font-semibold text-gray-800">
                        {user?.created_at
                          ? new Date(user.created_at).toLocaleDateString("en-IN")
                          : "-"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Video KYC */}
            {activeTab === "kyc" && (
              <div>
                <VideoKYC />
              </div>
            )}

            {/* Offers */}
            {activeTab === "offers" && (
              <div>
                <PreApprovedOffers />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
